import {
  AnalyticsOverview, HeatmapResponse, ConfigSnapshot,
  AnalyticsReport, AnalyticsReportSummary, FeatureUsageRate, CostEfficiency, InterventionEvent, PaginatedResponse,
} from '../types/analytics';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8081/api';

function getToken(): string {
  return localStorage.getItem('token') || '';
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const token = getToken();
  const response = await fetch(`${API_BASE}${url}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
    ...options,
  });
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('未登录');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  return response.json();
}

export const getAnalyticsOverview = (days = 7): Promise<AnalyticsOverview> =>
  request(`/admin/analytics/overview?days=${days}`);

export const getInterventionHeatmap = (days = 7): Promise<HeatmapResponse> =>
  request(`/admin/analytics/intervention-heatmap?days=${days}`);

export const getCostEfficiency = (days = 7): Promise<CostEfficiency> =>
  request(`/admin/analytics/cost-efficiency?days=${days}`);

export const getFeatureUsage = (): Promise<FeatureUsageRate[]> =>
  request('/admin/analytics/feature-usage');

export const listAnalyticsReports = (offset = 0, limit = 20): Promise<PaginatedResponse<AnalyticsReportSummary>> =>
  request(`/admin/analytics/reports?offset=${offset}&limit=${limit}`);

export const getAnalyticsReport = (id: string): Promise<AnalyticsReport> =>
  request(`/admin/analytics/reports/${encodeURIComponent(id)}`);

export const getAnalyticsReportStatus = (id: string): Promise<AnalyticsReportSummary> =>
  request(`/admin/analytics/reports/${encodeURIComponent(id)}/status`);

export const deleteAnalyticsReport = (id: string): Promise<{ ok: boolean }> =>
  request(`/admin/analytics/reports/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const triggerAnalyticsReport = (timeRangeStart?: string, timeRangeEnd?: string): Promise<{ ok: boolean; reportId: string; reused?: boolean }> =>
  request('/admin/analytics/reports', {
    method: 'POST',
    body: JSON.stringify({ timeRangeStart, timeRangeEnd }),
  });

export const listConfigSnapshots = (offset = 0, limit = 20): Promise<PaginatedResponse<ConfigSnapshot>> =>
  request(`/admin/analytics/snapshots?offset=${offset}&limit=${limit}`);

export const listAnalyticsEvents = (
  offset = 0, limit = 50, eventType?: string, eventAction?: string,
): Promise<PaginatedResponse<InterventionEvent>> => {
  let url = `/admin/analytics/events?offset=${offset}&limit=${limit}`;
  if (eventType) url += `&event_type=${encodeURIComponent(eventType)}`;
  if (eventAction) url += `&event_action=${encodeURIComponent(eventAction)}`;
  return request(url);
};
